import { memo, useCallback } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import type { FormFieldBaseProps } from './field-base';

export interface CheckboxGroupFieldProps extends FormFieldBaseProps<string[] | null | undefined> {}

export const CheckboxGroupField = memo(function CheckboxGroupField({
  field,
  value,
  onChange,
  readOnly = false,
  error,
}: CheckboxGroupFieldProps) {
  const { theme } = useTheme();
  const options = field.options ?? [];
  const current = Array.isArray(value) ? value : [];

  const toggle = useCallback(
    (optionValue: string) => {
      if (readOnly) return;
      const set = new Set(current);
      if (set.has(optionValue)) {
        set.delete(optionValue);
      } else {
        set.add(optionValue);
      }
      // Preserve option order for determinism.
      const ordered = options
        .map((o) => o.value)
        .filter((v) => set.has(v));
      onChange(ordered);
    },
    [current, options, onChange, readOnly],
  );

  const errorId = error ? `${field.key}__error` : undefined;
  return (
    <div className={'form-group checkbox-group-field' + (theme ? ' ' + theme : '') + (error ? ' has-error' : '')}>
      <label className={'field-label' + (field.required ? ' required' : '')}>
        {field.label}
      </label>
      <div className="checkbox-group-options" role="group" aria-invalid={error ? true : undefined} aria-describedby={errorId}>
        {options.map((option) => (
          <label key={option.value} className="checkbox-group-option">
            <input
              type="checkbox"
              name={field.key}
              value={option.value}
              checked={current.includes(option.value)}
              onChange={() => toggle(option.value)}
              disabled={readOnly}
            />
            {' '}
            {option.label}
          </label>
        ))}
      </div>
      {error ? <div id={errorId} className="form-field-error" role="alert">{error}</div> : null}
    </div>
  );
});
